import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { reactionAPI, platformAPI } from '../services/api';
import { buildInviteLink } from '../utils/inviteLinks';
import '../styles/Dashboard.css';
import '../styles/Reaction.css';

const CREATE_KIND_FRIENDLY = 'friendly';
const CREATE_KIND_REAL = 'real';

export default function ReactionLobby() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [openMatches, setOpenMatches] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [ongoingMatches, setOngoingMatches] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendsOnlineStatus, setFriendsOnlineStatus] = useState({});

  const [wager, setWager] = useState(50);
  const [minimumWager, setMinimumWager] = useState(50);
  const [opponentId, setOpponentId] = useState('');
  const [durationMs, setDurationMs] = useState(60_000);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKind, setCreateKind] = useState(CREATE_KIND_FRIENDLY);
  const [activeTab, setActiveTab] = useState('my');
  const [roomSearch, setRoomSearch] = useState('');
  const [joinId, setJoinId] = useState('');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const durationOptions = [30, 45, 60, 90, 120];

  useEffect(() => {
    const loadPlatformSettings = async () => {
      try {
        const res = await platformAPI.getSettings();
        const nextMin = Number(res?.data?.minimumWager);
        if (Number.isFinite(nextMin) && nextMin >= 0) {
          setMinimumWager(nextMin);
          setWager((prev) => Math.max(nextMin, Number(prev) || 0));
        }
      } catch {
        // ignore settings failure
      }
    };

    loadPlatformSettings();
  }, []);

  const userId = Number(user?.id || 0);

  const allMatches = useMemo(() => {
    const combinedRaw = [...ongoingMatches, ...challenges, ...openMatches];
    const combinedMap = new Map();

    combinedRaw.forEach((m) => {
      const id = String(m?.id || '');
      if (!id) return;
      if (!combinedMap.has(id)) combinedMap.set(id, m);
    });

    return Array.from(combinedMap.values()).sort((a, b) => {
      const ta = Date.parse(a?.started_at || a?.created_at || '') || Number(a?.id || 0);
      const tb = Date.parse(b?.started_at || b?.created_at || '') || Number(b?.id || 0);
      return tb - ta;
    });
  }, [ongoingMatches, challenges, openMatches]);

  const myCreatedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.creator_id) === userId),
    [allMatches, userId]
  );

  const myJoinedMatches = useMemo(
    () => allMatches.filter((m) => Number(m.opponent_id) === userId && Number(m.creator_id) !== userId),
    [allMatches, userId]
  );

  const availableMatches = useMemo(
    () => allMatches.filter((m) => Number(m.creator_id) !== userId && !m.opponent_id),
    [allMatches, userId]
  );

  const filteredAvailableMatches = useMemo(() => {
    const query = String(roomSearch || '').trim().toLowerCase();
    if (!query) return availableMatches;

    return availableMatches.filter((m) => {
      const id = String(m?.id || '').toLowerCase();
      const creator = String(m?.creator_username || '').toLowerCase();
      return id.includes(query) || creator.includes(query);
    });
  }, [availableMatches, roomSearch]);

  const matchByJoinCode = useMemo(
    () => allMatches.find((m) => String(m.id) === String(joinId || '').trim()),
    [allMatches, joinId]
  );

  const canCreate = useMemo(() => {
    const w = Number(wager);
    if (!Number.isFinite(w) || w < 0) return false;
    if (createKind === CREATE_KIND_REAL && w < minimumWager) return false;
    return (user?.balance ?? 0) >= w;
  }, [wager, user, createKind, minimumWager]);

  const loadOpen = async () => {
    try {
      const res = await reactionAPI.listOpenMatches();
      setOpenMatches(res.data || []);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.error;
      if (status === 401) setError('Session expired. Please login again.');
      else if (msg) setError(msg);
    }
  };

  const loadChallenges = async () => {
    try {
      const res = await reactionAPI.listChallenges();
      setChallenges(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) setError('Session expired. Please login again.');
      else if (e?.response?.status >= 400) {
        console.error('Failed to load challenges:', e.response?.data?.error || e.message);
      }
    }
  };

  const loadOngoing = async () => {
    try {
      const res = await reactionAPI.listOngoingMatches();
      setOngoingMatches(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) setError('Session expired. Please login again.');
      else if (e?.response?.status >= 400) {
        console.error('Failed to load ongoing matches:', e.response?.data?.error || e.message);
      }
    }
  };

  const loadFriends = async () => {
    try {
      const res = await api.get('/friends/online-status');
      setFriends(res.data || []);
    } catch {
      try {
        const res = await api.get('/friends');
        setFriends(res.data || []);
      } catch {
        // non-fatal
      }
    }
  };

  const loadAll = async () => {
    try {
      setError('');
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing(), loadFriends()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load lobby');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    const id = setInterval(() => {
      void loadOpen();
      void loadChallenges();
      void loadOngoing();
    }, 2500);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshUser?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareMatchLink = async (matchId, roomType = 'challenge') => {
    if (!matchId) return;
    const link = buildInviteLink(`/reaction/duel/${matchId}`);
    const message = `Join my Color Arena ${roomType}: ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Color Arena Invite', text: message, url: link });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        window.prompt('Share this Color Arena invite link:', link);
      }
      setError('');
    } catch {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(link);
        } else {
          window.prompt('Share this Color Arena invite link:', link);
        }
      } catch {
        window.prompt('Share this Color Arena invite link:', link);
      }
    }
  };

  const handleCreate = async (kind = createKind, opts = {}) => {
    try {
      setError('');
      const w = kind === CREATE_KIND_REAL ? Math.max(0, Number(wager || 0)) : 0;
      if (kind === CREATE_KIND_REAL && w < minimumWager) {
        setError(`Wager must be at least ${minimumWager} wikipenny`);
        return false;
      }
      const opp = String(opponentId || '').trim();
      const safeOpponentId = opp ? Number(opp) : null;

      const res = await reactionAPI.createMatch({
        wager: w,
        roundsTotal: 1,
        opponentId: safeOpponentId,
        gameMode: 'color_confusion',
        durationMs,
      });
      const shouldShare = Boolean(opts.shareAfterCreate);
      if (shouldShare) {
        await shareMatchLink(res.data.id, kind === CREATE_KIND_REAL ? 'bet room' : 'friendly room');
      }
      refreshUser?.();
      navigate(`/reaction/duel/${res.data.id}`);
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to create match');
      return false;
    }
  };

  const handleJoin = async (matchId) => {
    try {
      setError('');
      const res = await reactionAPI.joinMatch(matchId);
      refreshUser?.();
      navigate(`/reaction/duel/${res.data.id}`);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to join match');
    }
  };

  const handleCancel = async (matchId) => {
    try {
      setError('');
      await reactionAPI.cancelMatch(matchId);
      refreshUser?.();
      await Promise.allSettled([loadOpen(), loadChallenges(), loadOngoing()]);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to cancel match');
    }
  };

  const getFlags = (m) => {
    const isIncoming = Number(m.opponent_id) === userId;
    const isCreator = Number(m.creator_id) === userId;
    const status = String(m.status || 'pending');
    const needsAccept = isIncoming && !m.accepted_at && status === 'pending';
    const canCancel = isCreator && status === 'pending' && !m.started_at;
    const isOngoing = status === 'in_progress';
    return { needsAccept, canCancel, isOngoing };
  };

  const getStatusBadge = (status, isOngoing) => {
    if (isOngoing) return <span className="status-badge ongoing">In Progress</span>;
    if (status === 'pending') return <span className="status-badge pending">Waiting</span>;
    return <span className="status-badge completed">Completed</span>;
  };

  const onJoinByCode = async () => {
    const code = String(joinId || '').trim();
    if (!code) return;
    const match = allMatches.find((m) => String(m.id) === code);
    if (!match) {
      setError('Match not found');
      return;
    }

    const isCreator = Number(match.creator_id) === userId;
    const isOpponent = Number(match.opponent_id) === userId;
    const status = String(match.status || 'pending');

    if (!match.opponent_id && !isCreator) {
      await handleJoin(match.id);
      return;
    }

    if (status === 'pending' || status === 'in_progress' || isCreator || isOpponent) {
      navigate(`/reaction/duel/${match.id}`);
      return;
    }

    setError('You cannot join this match right now.');
  };

  return (
    <div className="dashboard-container dashboard-redesign reaction-lobby">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="dashboard-content">
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon">CC</div>
            <div>
              <h1 className="page-title">Color Arena</h1>
              <p className="page-subtitle">Color Confusion challenge lobby with same arena flow</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>

        <div className="user-stats-card">
          <div className="stats-row">
            <div className="stat-item">
              <div className="stat-icon">$</div>
              <div className="stat-info">
                <div className="stat-label">Balance</div>
                <div className="stat-value">{user?.balance ?? 0} credits</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">T</div>
              <div className="stat-info">
                <div className="stat-label">Round Time</div>
                <div className="stat-value">{Math.round(durationMs / 1000)}s</div>
              </div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-icon">A</div>
              <div className="stat-info">
                <div className="stat-label">Active Games</div>
                <div className="stat-value">{myCreatedMatches.filter((m) => m.status === 'in_progress').length + myJoinedMatches.filter((m) => m.status === 'in_progress').length}</div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-alert">
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>x</button>
          </div>
        )}

        <div className="quick-actions-card">
          <div className="quick-actions-row">
            <div className="join-by-code">
              <input
                className="code-input"
                value={joinId}
                onChange={(e) => setJoinId(e.target.value)}
                placeholder="Enter match ID"
              />
              <button className="join-btn" disabled={!joinId} onClick={onJoinByCode}>
                Join Match
              </button>
            </div>
          </div>
          {matchByJoinCode && (
            <div className="match-found">
              <span className="found-icon">🎯</span>
              Match found: {matchByJoinCode.creator_username || 'Host'} vs {matchByJoinCode.opponent_username || 'Open'}
            </div>
          )}
        </div>

        <div className="create-challenges">
          <div className="section-header">
            <h2 className="section-title">Create Challenge</h2>
            <p className="section-subtitle">Pick your preferred match type</p>
          </div>
          <div className="create-cards">
            <div className="create-card bot" onClick={() => navigate('/reaction/practice')}>
              <div className="create-icon">PR</div>
              <div className="create-info">
                <div className="create-title">Practice Mode</div>
                <div className="create-desc">Practice without wager</div>
              </div>
              <div className="create-arrow">-></div>
            </div>
            <div className="create-card friendly" onClick={() => { setCreateKind(CREATE_KIND_FRIENDLY); setShowCreateModal(true); }}>
              <div className="create-icon">FR</div>
              <div className="create-info">
                <div className="create-title">Friendly Challenge</div>
                <div className="create-desc">Open room or invite friend</div>
              </div>
              <div className="create-arrow">-></div>
            </div>
            <div className="create-card real" onClick={() => { setCreateKind(CREATE_KIND_REAL); setShowCreateModal(true); }}>
              <div className="create-icon">RC</div>
              <div className="create-info">
                <div className="create-title">Real Challenge</div>
                <div className="create-desc">Minimum wager required</div>
              </div>
              <div className="create-arrow">-></div>
            </div>
          </div>
        </div>

        <div className="matches-section">
          <div className="matches-tabs">
            <button className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`} onClick={() => setActiveTab('my')}>
              My Games
              <span className="tab-count">{myCreatedMatches.length + myJoinedMatches.length}</span>
            </button>
            <button className={`tab-btn ${activeTab === 'available' ? 'active' : ''}`} onClick={() => setActiveTab('available')}>
              Available
              <span className="tab-count">{availableMatches.length}</span>
            </button>
          </div>

          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading matches...</span>
            </div>
          )}

          {!loading && activeTab === 'my' && (
            <>
              {[...myCreatedMatches, ...myJoinedMatches].length === 0 && (
                <div className="empty-state">
                  <div className="empty-title">No active games</div>
                  <div className="empty-desc">Create a challenge or join an available game</div>
                </div>
              )}

              <div className="matches-list">
                {[...myCreatedMatches, ...myJoinedMatches].map((m) => {
                  const flags = getFlags(m);
                  const isBetRoom = Number(m.wager || 0) > 0;
                  const isCreatedByMe = Number(m.creator_id) === userId;
                  return (
                    <div key={`mine-${m.id}`} className="match-card">
                      <div className="match-header">
                        <div className="match-id">Match #{m.id}</div>
                        <div className="match-badges">
                          <span className={`mode-badge ${isBetRoom ? 'real' : 'friendly'}`}>{isBetRoom ? 'Real' : 'Friendly'}</span>
                          {isBetRoom && <span className="wager-badge">Wager: {m.wager}</span>}
                          {getStatusBadge(m.status, flags.isOngoing)}
                        </div>
                      </div>
                      <div className="match-players">
                        <div className="player">
                          <span className="player-role">Host</span>
                          <span className="player-name">{m.creator_username || '-'}</span>
                        </div>
                        <div className="vs-divider">VS</div>
                        <div className="player">
                          <span className="player-role">Opponent</span>
                          <span className="player-name">{m.opponent_username || 'Open'}</span>
                        </div>
                      </div>
                      <div className="match-details">
                        <span className="detail-item">Time: {Math.round((m.duration_ms || 60000) / 1000)}s</span>
                        <span className="detail-item">Round: {m.current_round || 1}/{m.rounds_total || 1}</span>
                      </div>
                      {isCreatedByMe && (
                        <div className="match-invite-row">
                          <button
                            className="action-btn watch"
                            onClick={() => shareMatchLink(m.id, isBetRoom ? 'bet room' : 'friendly room')}
                            title="Copy invite link"
                            aria-label="Copy invite link"
                          >
                            📋
                          </button>
                        </div>
                      )}
                      <div className="match-actions">
                        {flags.needsAccept && (
                          <button
                            className="action-btn join"
                            onClick={async () => {
                              try {
                                setError('');
                                await reactionAPI.acceptMatch(m.id);
                                await loadChallenges();
                                refreshUser?.();
                                navigate(`/reaction/duel/${m.id}`);
                              } catch (e) {
                                setError(e.response?.data?.error || 'Failed to accept');
                              }
                            }}
                          >
                            Accept ->
                          </button>
                        )}
                        <button className="action-btn play" onClick={() => navigate(`/reaction/duel/${m.id}`)}>
                          {flags.isOngoing ? 'Resume ->' : 'Open ->'}
                        </button>
                        {flags.canCancel && <button className="action-btn cancel" onClick={() => handleCancel(m.id)}>Cancel</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!loading && activeTab === 'available' && (
            <>
              <div className="search-bar">
                <span className="search-icon">🔍</span>
                <input
                  className="search-input"
                  value={roomSearch}
                  onChange={(e) => setRoomSearch(e.target.value)}
                  placeholder="Search by match ID or player name..."
                />
                {roomSearch && (
                  <button className="clear-search" onClick={() => setRoomSearch('')}>×</button>
                )}
              </div>

              {filteredAvailableMatches.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-title">No available challenges</div>
                  <div className="empty-desc">
                    {roomSearch ? `No matches matching "${roomSearch}"` : 'Create a challenge to start playing'}
                  </div>
                  {roomSearch && (
                    <button className="clear-filters-btn" onClick={() => setRoomSearch('')}>Clear Search</button>
                  )}
                </div>
              ) : (
                <div className="matches-list">
                  {filteredAvailableMatches.map((m) => {
                    const isCreator = Number(m.creator_id) === userId;
                    const canCancel = isCreator && String(m.status || 'pending') === 'pending' && !m.started_at;
                    const isBetRoom = Number(m.wager || 0) > 0;
                    return (
                      <div key={`open-${m.id}`} className="match-card available">
                        <div className="match-header">
                          <div className="match-id">Match #{m.id}</div>
                          <div className="match-badges">
                            <span className={`mode-badge ${isBetRoom ? 'real' : 'friendly'}`}>{isBetRoom ? 'Real' : 'Friendly'}</span>
                            {isBetRoom && <span className="wager-badge">Wager: {m.wager}</span>}
                          </div>
                        </div>
                        <div className="match-players">
                          <div className="player">
                            <span className="player-role">Host</span>
                            <span className="player-name">{m.creator_username || '-'}</span>
                          </div>
                          <div className="vs-divider">VS</div>
                          <div className="player">
                            <span className="player-role">Open</span>
                            <span className="player-name waiting">Waiting for opponent</span>
                          </div>
                        </div>
                        <div className="match-details">
                          <span className="detail-item">Time: {Math.round((m.duration_ms || 60000) / 1000)}s</span>
                          <span className="detail-item">Rounds: {m.rounds_total || 1}</span>
                        </div>
                        <div className="match-actions">
                          {!isCreator && <button className="action-btn join" onClick={() => handleJoin(m.id)}>Join -></button>}
                          {canCancel && <button className="action-btn cancel" onClick={() => handleCancel(m.id)}>Cancel</button>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {showCreateModal && (
          <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-container chess-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">{createKind === CREATE_KIND_REAL ? 'Real Challenge' : 'Friendly Challenge'}</h3>
                <button className="modal-close" onClick={() => setShowCreateModal(false)}>x</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Time Control</label>
                  <div className="time-options">
                    {durationOptions.map((sec) => (
                      <button
                        key={sec}
                        className={`time-option ${durationMs === sec * 1000 ? 'selected' : ''}`}
                        onClick={() => setDurationMs(sec * 1000)}
                      >
                        <span className="time-icon">TM</span>
                        <span className="time-label">{sec}s</span>
                      </button>
                    ))}
                  </div>
                </div>

                {createKind === CREATE_KIND_REAL && (
                  <div className="form-group">
                    <label className="form-label">Wager Amount</label>
                    <div className="wager-input-wrapper">
                      <span className="wager-currency">$</span>
                      <input
                        type="number"
                        className="form-input wager-input"
                        min={minimumWager}
                        max={user?.balance ?? 0}
                        value={wager}
                        onChange={(e) => setWager(Number(e.target.value))}
                      />
                      <span className="wager-suffix">credits</span>
                    </div>
                    <div className="form-hint">Minimum: {minimumWager} wikipenny - Balance: {user?.balance ?? 0} credits</div>
                    {!canCreate && (
                      <div className="error-hint">
                        {Number(wager) < minimumWager
                          ? `Minimum wager is ${minimumWager} wikipenny`
                          : 'Insufficient balance for this wager'}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Invite Friend (Optional)</label>
                  <select className="form-select" value={opponentId} onChange={(e) => setOpponentId(e.target.value)}>
                    <option value="">Open match (anyone can join)</option>
                    {friends.map((f) => (
                      <option key={f.friendship_id || f.friend_id} value={f.friend_id}>
  {Boolean(friendsOnlineStatus[f.friend_id]) ? '🟢' : '⚪'} | {f.friend_username || f.username || 'Friend'}
</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-create" onClick={async () => {
                  const created = await handleCreate(createKind);
                  if (created) setShowCreateModal(false);
                }} disabled={createKind === CREATE_KIND_REAL && !canCreate}>
                  Create Challenge ->
                </button>
                <button className="join-btn" onClick={async () => {
                  const created = await handleCreate(createKind, { shareAfterCreate: true });
                  if (created) setShowCreateModal(false);
                }} disabled={createKind === CREATE_KIND_REAL && !canCreate}>
                  Create & Copy Link
                </button>
                <button className="btn-cancel" onClick={() => setShowCreateModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
